import React, { useState } from 'react';
import { History, User, LogIn, Plus, Pencil, Trash2, Upload, ShieldAlert, Filter, RefreshCw } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { auditApi } from '../lib/api';

const ACTION_META = {
  login: { label: 'Login', color: '#15803d', bg: '#dcfce7', icon: LogIn },
  login_failed: { label: 'Login fallito', color: '#b91c1c', bg: '#fee2e2', icon: ShieldAlert },
  create: { label: 'Creazione', color: '#023F80', bg: '#e0e7ff', icon: Plus },
  update: { label: 'Modifica', color: '#b45309', bg: '#fef7d4', icon: Pencil },
  delete: { label: 'Eliminazione', color: '#b91c1c', bg: '#fee2e2', icon: Trash2 },
  bulk_create: { label: 'Creazione bulk', color: '#023F80', bg: '#e0e7ff', icon: Plus },
  bulk_import: { label: 'Import Excel', color: '#b45309', bg: '#fef7d4', icon: Upload },
};

const ENTITY_LABEL = {
  auth: 'Autenticazione',
  users: 'Utente',
  companies: 'Azienda',
  workers: 'Lavoratore',
  formazione: 'Formazione',
  visite: 'Visita medica',
  dpi: 'DPI',
  haccp: 'HACCP',
  sicurezza: 'Sicurezza',
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const summarize = (item) => {
  if (item.action === 'update' && Array.isArray(item.details)) {
    return `Campi: ${item.details.join(', ')}`;
  }
  if (item.action.startsWith('bulk_') && item.details?.count != null) {
    return `${item.details.count} record`;
  }
  if (item.action === 'login_failed') return item.details || 'Credenziali errate';
  const before = item.before || {};
  const after = item.after || {};
  const name = after.name || after.firstName || after.lastName || after.corso || after.tipo || before.name || before.firstName || before.corso || '';
  return name ? `"${[after.firstName || before.firstName, after.lastName || before.lastName].filter(Boolean).join(' ').trim() || name}"` : '';
};

const AuditPage = () => {
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const params = {
    limit: 100,
    ...(actionFilter !== 'all' ? { action: actionFilter } : {}),
    ...(entityFilter !== 'all' ? { entityType: entityFilter } : {}),
  };
  const { data, loading, reload } = useFetch(() => auditApi.list(params), [actionFilter, entityFilter, refreshKey]);

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
              <History size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800 leading-tight">Audit log</h2>
              <p className="text-xs text-slate-500 mt-0.5">Tracciamento completo delle azioni effettuate su SafetyDesk81: login, creazioni, modifiche, eliminazioni, import.</p>
            </div>
          </div>
          <button onClick={() => { setRefreshKey((k) => k + 1); reload(); }} data-testid="audit-refresh" className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-2">
            <RefreshCw size={13} /> Aggiorna
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Azione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le azioni</SelectItem>
              {Object.entries(ACTION_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger data-testid="filter-entity"><SelectValue placeholder="Entità" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le entità</SelectItem>
              {Object.entries(ENTITY_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 flex items-center" data-testid="audit-total">
            {data?.total != null ? `${data.items?.length || 0} di ${data.total} eventi` : '—'}
          </div>
        </div>

        {loading ? <Spinner /> : (
          <div className="space-y-1.5" data-testid="audit-list">
            {(data?.items || []).map((it) => {
              const meta = ACTION_META[it.action] || { label: it.action, color: '#64748b', bg: '#e2e8f0', icon: History };
              const Icon = meta.icon;
              const ent = ENTITY_LABEL[it.entityType] || it.entityType;
              return (
                <div key={it.id} className="flex items-center gap-3 px-3 py-2.5 border border-slate-100 rounded-xl hover:bg-slate-50/60" data-testid={`audit-${it.id}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-bold text-gray-800">{it.username || 'anonimo'}</span>
                      <span className="text-slate-400">·</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                      <span className="text-slate-600">{ent}</span>
                      {it.entityId && <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{it.entityId}</code>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{summarize(it)}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 shrink-0">{fmt(it.at)}</div>
                </div>
              );
            })}
            {!data?.items?.length && <div className="py-12 text-center text-sm text-slate-400">Nessun evento</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditPage;
